import {computed, MaybeRefOrGetter, toValue} from "vue";
import {storeToRefs} from "pinia";
import {MSSantePrefill, SurveySecureMessagingPrefill} from "@/mssante/interfaces/SecureMessaging";
import {usePatientStore} from "@/stores/patient.store";
import formatHelper from "@/core/helpers/formatHelper";

/**
 * Turns the MSSanté messages the API renders for a survey into dialog prefills,
 * filling the patient placeholders the API leaves to the client.
 *
 * @param consumerKey The consumer the survey was answered for.
 */
const useSurveyMSSantePrefills = (consumerKey: MaybeRefOrGetter<string | undefined>) => {
    const {customer} = storeToRefs(usePatientStore());

    /**
     * The patient, only when the loaded one is the survey respondent: the
     * placeholders must never be filled with another patient identity.
     */
    const patient = computed(() => {
        return customer.value?.consumer_key === toValue(consumerKey) ? customer.value : undefined;
    });

    /**
     * The patient placeholders the API leaves for the client to resolve. The INS is
     * not exposed to Connect yet, so it resolves to an empty string.
     */
    const placeholders = computed((): Record<string, string> => ({
        patient_civility: formatHelper.civility(patient.value?.information?.gender),
        patient_full_name: patient.value?.information?.name ?? '',
        patient_birth_date: patient.value?.information?.formatted_birth_date ?? '',
        patient_ins: '',
    }));

    /**
     * Resolve the patient placeholders, dropping the ones we cannot fill so no raw
     * placeholder ends up in a sent message.
     */
    const resolve = (content: string): string => {
        return Object.entries(placeholders.value)
            .reduce((text, [key, value]) => text.replaceAll(`{{$${key}}}`, value), content)
            .replace(/{{\$[a-z_]+}}/g, '');
    };

    /** The messages to propose, in the order the API returned them. */
    const toPrefills = (rendered: SurveySecureMessagingPrefill[]): MSSantePrefill[] => rendered.map(prefill => ({
        audience: prefill.audience,
        subject: resolve(prefill.subject),
        body: resolve(prefill.body),
        documents: prefill.attachments,
    }));

    return {patient, toPrefills};
};

export default useSurveyMSSantePrefills;
